import { calculateCreditsPerBillingCycle } from "../../_utils/pricing";

const TIERED_MODELS = ["Volume", "Tiered"];

/**
 * `usePricingState`'s plans/products/lineItems/discount/taxes are held (and
 * edited) in a flat, engine-internal shape - see `PlanProductPicker.jsx`'s
 * `BLANK_ITEM`/`priceItem`. Every backend schema that actually stores this
 * data (`Subscription`, `Proposal`, `Invoice` in `@Simplifyhq/shared-backend`)
 * expects a different, nested shape instead. Mongoose runs these schemas in
 * strict mode with no reshaping setters/virtuals, and every controller that
 * writes them (`createSubscription`/`editSubscription`, Proposal's create/
 * edit, Invoice's) passes `req.body` straight through - so every field that
 * doesn't match a schema path gets silently stripped on save, and the plan/
 * product's real catalog `_id` reference is lost entirely (a fresh, unrelated
 * ObjectId gets auto-generated instead, since the engine only ever sends
 * `entityId`). This was a live, unnoticed data-corruption bug for every
 * Subscription/Proposal/Invoice created or edited since the new engine
 * shipped - confirmed by reading the schema + controller source directly,
 * not inferred. Proposal's `mrr` virtual and its PDF-generation helpers both
 * destructure `plan.accordionData` directly, so on top of losing the data
 * silently, those throw a TypeError whenever the corrupted shape is read
 * back.
 *
 * This is the fix - one shared adapter (not three separate copies, since the
 * mismatch and the correct shape are identical across all three consumers)
 * applied at the exact submit boundary. `usePricingState`/`PlanProductPicker`
 * /`LineItemsEditor`/`DiscountEditor`/`TaxEditor` are all left untouched -
 * their internal state stays flat, which is simpler; only what leaves the
 * app at submit time changes shape.
 */

// A plan/product's `unitPrice` is 0 for Tiered/Volume models (no single
// per-unit rate applies - matches the old BillingItems/PlanProductTable's
// own convention, confirmed in its `handlePlanSelect`), else the raw
// catalog `amount`. `price` reuses the engine's own `basePrice` (already the
// correct pre-discount, cycle-converted total - no new math). Correct
// `creditsPerBillingCycle` never gets STORED on the engine item at all (it's
// a display-only computed column in `PlanProductPicker`) - the adapter
// computes it once via the same tested helper the column display already
// calls, rather than an ad hoc recomputation.
function toAccordionItem(item, { selectedBillingCycle }) {
  const isTieredOrVolume = TIERED_MODELS.includes(item.pricingModel);
  const creditsPerBillingCycle = calculateCreditsPerBillingCycle(
    item.quantity,
    item.pricingCycle,
    selectedBillingCycle,
  );

  const accordionItem = {
    _id: item.entityId,
    name: item.label,
    pricingModel: item.pricingModel,
    // The Subscription schema spreads the FULL `planSchema.obj` onto each
    // embedded plan (`...planSchema.obj` in Subscription.js), which requires
    // `planName`/`description`/`billingCycle` - discovered live (not from
    // static reading) when a real submit 400'd with "plans.0.description:
    // Path `description` is required." `planName`/`billingCycle` are real
    // data the engine item already carries under different names.
    // `description` has no real source on the engine item at all (dropped
    // the moment `handleEntityChange` picks an entity in
    // `PlanProductPicker.jsx`) - Mongoose's `required: true` rejects an
    // empty string too (confirmed live), so this falls back to the plan's
    // own name rather than fabricated placeholder text; not the plan's
    // actual description copy, which the engine never carries through -
    // flagged in known-issues.md rather than threading the full catalog
    // through 3 hook signatures for one cosmetic field. Harmless on
    // `products` (neither schema requires these there, strict mode just
    // drops the extras).
    planName: item.label,
    // `Subscription.js`'s embedded PRODUCT sub-schema is the one place that
    // spreads `...productSchema.obj` instead of declaring its own `name`
    // literal (Invoice/Proposal/UpsellDownsell all declare `name` directly,
    // confirmed by reading all four schemas) - `productSchema.obj` declares
    // `productName`, not `name`/`planName`, so without this a Subscription's
    // saved product silently ended up with NO name field at all (same
    // strict-mode-drops-unknown-fields root cause as the original
    // corruption bug, just narrower - only Subscription products, only the
    // name). Setting it unconditionally alongside `planName` is harmless
    // everywhere else: whichever schema doesn't declare a given key just
    // drops it, same as `planName` already being harmless on products.
    productName: item.label,
    description: item.label || "-",
    billingCycle: item.pricingCycle,
    // Same required-field gap as planName/description/billingCycle above,
    // just not caught until later: Subscription's embedded plan/product
    // schema requires `walletCredits` too. `item.freeCredits` already IS the
    // catalog's `walletCredits` value under a different name (see
    // PlanProductPicker.jsx's handleEntityChange - `freeCredits:
    // entity.walletCredits || 0`), so this isn't fabricated data, just
    // surfacing it under the schema's real field name. Confirmed live: a
    // Subscription saved without this validates fine (schema default 0
    // applies on `.save()`), but re-embedding that same plan into a new
    // UpsellDownsell snapshot later - after an upsell execution round-trips
    // it through a `$set` update, which doesn't apply defaults the same way
    // - 400'd with "subscription.plans.0.walletCredits: Path `walletCredits`
    // is required."
    walletCredits: item.freeCredits || 0,
    // Same silent-drop shape as the fields documented above - Subscription's
    // embedded plan schema stores this as a plain ObjectId ref (see
    // processUsageAndUpdateWallet.js's SUBSCRIPTION_POPULATE comment), and
    // without it the usage-metering pipeline has nothing to read: the
    // subscription looks correctly priced/walleted but never receives a
    // single usage debit. Undefined on a Product's engine item is fine -
    // Product's own schema has no usageCompilations path, strict mode drops
    // it same as planName does there.
    usageCompilations: item.usageCompilations || undefined,
    accordionData: {
      unitPrice: isTieredOrVolume ? 0 : item.amount,
      quantity: item.quantity,
      price: item.basePrice,
      discountType: item.discountType,
      discount: item.discount,
      totalPrice: item.totalPrice,
      freeCredits: item.freeCredits,
      creditsPerBillingCycle,
      pricingTiers: item.pricingTiers,
    },
  };

  // `selectedBundle` is plans-only (no `products[].selectedBundle` path on
  // any of the three schemas) and only relevant once a bundle's actually
  // been picked. Only `_id`/`name` are populated - the schema also declares
  // `orgId` and a denormalized `plans: [{_id, planName}]` snapshot of the
  // bundle's own contents, but neither is available on the picker's item
  // (the bundle catalog response itself doesn't carry a plans list, verified
  // against `bundle.controller.js`/`Bundles.js` - not fabricating those
  // fields with guessed values).
  if (item.bundleId) {
    accordionItem.selectedBundle = { _id: item.bundleId, name: item.bundleName };
  }

  return accordionItem;
}

function toLineItem(item) {
  return {
    name: item.label,
    quantity: item.quantity,
    value: item.value,
    discountType: item.discountType,
    discount: item.discount,
    totalPrice: item.totalPrice,
    discountApplied: item.discountApplied,
    // Only ever set on rows edited through LineItemsEditor's Invoice-only
    // `showBillingType` column (Recurring/One time) - undefined for Proposal/
    // Subscription callers, same as before; harmless there since none of
    // their schemas' billingType path enforces a required value.
    billingType: item.billingType,
  };
}

// Every schema declares `discount` as an array of `{ value, type }`, not the
// single object `usePricingState` holds it as.
function toDiscountArray(discount) {
  return [{ type: discount?.type || "Flat", value: discount?.value ?? "" }];
}

// `taxValue` -> `tax` (the schema's field name for the rate/value); `amount`
// (the pre-tax total this tax was calculated against) doesn't exist on the
// engine's tax item at all - it's carried separately on `pricing.totals`.
// `name` isn't a declared schema path on any of the 4 consumers' tax
// sub-schemas (they all use `taxLabel`) - included anyway (harmless, strict
// mode drops it) so a raw `toBillingSubmitPayload` call can also serve as a
// display/comparison shape without a caller having to know that detail;
// Upsell/Downsell's review step (`UpsellReviewStep.jsx`) matches/labels
// taxes by `.name`, same as it does for lineItems.
function toTax(tax, { totalAmountBeforeTax }) {
  return {
    amount: totalAmountBeforeTax,
    tax: tax.taxValue,
    taxLabel: tax.taxLabel,
    name: tax.taxLabel,
    taxType: tax.taxType,
    totalTax: tax.totalTax,
  };
}

/**
 * The one function consumers actually call - composes the four mappers
 * above over a `usePricingState` instance's current collections into the
 * exact shape `createSubscription`/`editSubscription`, Proposal's create/
 * edit, and Invoice's create/edit all expect. Call this at submit time only;
 * nothing about the picker's own editing/rendering needs it.
 */
export function toBillingSubmitPayload(pricing, { selectedBillingCycle } = {}) {
  return {
    plans: pricing.plans.map((item) => toAccordionItem(item, { selectedBillingCycle })),
    products: pricing.products.map((item) => toAccordionItem(item, { selectedBillingCycle })),
    lineItems: pricing.lineItems.map(toLineItem),
    discount: toDiscountArray(pricing.discount),
    taxes: pricing.taxes.map((tax) =>
      toTax(tax, { totalAmountBeforeTax: pricing.totals.totalAmountBeforeTax }),
    ),
  };
}
